import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { SUBSCRIPTION_CHECKOUT_CART_QUERY_KEY } from '@/state/queries/transactions/use-subscription-checkout-cart';
import type { SubscriptionCheckoutCartResponse } from '@/state/queries/transactions/use-subscription-checkout-cart';
import type { SubscriptionBillingPeriod, SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';

export type PatchOrganizationSubscriptionCheckoutCartBody = {
  clear?: boolean;
  lines?: {
    subscriptionProductKey: string;
    /** Omit when the Organization catalog has exactly one tier for this product (server infers). */
    tier?: SubscriptionTier;
    billingPeriod?: SubscriptionBillingPeriod;
    additionalQuotaUnits?: { quotaKey: string; quantity: number }[];
    parentSubscriptionInstanceId?: string;
  }[];
  quotaAddons?: { quotaKey: string; quantity: number }[];
};

export function usePatchOrganizationSubscriptionCheckoutCart() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { organizationId: string; body: PatchOrganizationSubscriptionCheckoutCartBody }) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/transactions/organization/${payload.organizationId}/subscription-checkout-cart`;
      return sendJSONRequest({
        url,
        method: METHOD.PATCH,
        body: payload.body,
        token: token ?? '',
      }) as Promise<SubscriptionCheckoutCartResponse>;
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_CHECKOUT_CART_QUERY_KEY, variables.organizationId],
      });
    },
  });
}
